const META_SELECTOR = '[data-langley-cart-parity-data]';

const infoIcon = `
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.6" />
    <path d="M12 10.5V16" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
    <circle cx="12" cy="7.5" r="1" fill="currentColor" />
  </svg>`;

const lockIcon = `
  <svg data-langley-secure-lock viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="5.5" y="10" width="13" height="10" rx="1.6" stroke="currentColor" stroke-width="1.7" />
    <path d="M8.5 10V7.8a3.5 3.5 0 0 1 7 0V10" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
  </svg>`;

function parseMeta(script) {
  try {
    return JSON.parse(script.textContent || '{}');
  } catch (_error) {
    return null;
  }
}

function getContext(script) {
  const section = script.closest('.shopify-section') || document;
  const drawer = section.querySelector('cart-drawer-component') || document.querySelector('cart-drawer-component');
  return { section, drawer };
}

function remainingLabelFromMessage(progress) {
  const text = progress.querySelector('.langley-delivery-progress__message')?.textContent?.trim() || '';
  const patterns = [
    /^You're\s+(.+?)\s+away from complimentary UK delivery[.!]?$/i,
    /^Add\s+(.+?)\s+more to unlock complimentary UK delivery[.!]?$/i,
    /^Almost there\s*[—-]\s*add\s+(.+?)\s+more for complimentary UK delivery[.!]?$/i,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match?.[1]) return match[1];
  }

  return '';
}

function parseMoneyMinor(text) {
  const cleaned = String(text || '')
    .replace(/\u00a0/g, ' ')
    .replace(/[^\d,.-]/g, '');

  if (!cleaned) return null;

  const comma = cleaned.lastIndexOf(',');
  const dot = cleaned.lastIndexOf('.');
  let normalized = cleaned;

  if (comma > dot) {
    normalized = cleaned.replace(/\./g, '').replace(',', '.');
  } else {
    normalized = cleaned.replace(/,/g, '');
  }

  const value = Number.parseFloat(normalized);
  return Number.isFinite(value) ? Math.round(value * 100) : null;
}

function currentSubtotalMinor(drawer, progress) {
  const rows = drawer.querySelectorAll('.langley-cart__summary .langley-cart__summary-row');

  for (const row of rows) {
    const label = row.firstElementChild?.textContent?.trim().toLowerCase();
    if (label !== 'subtotal') continue;

    const parsed = parseMoneyMinor(row.lastElementChild?.textContent || '');
    if (parsed !== null) return parsed;
  }

  return Number(progress.dataset.deliverySubtotalMinor || 0);
}

function currentSubtotalLabel(drawer, fallback = '') {
  const rows = drawer.querySelectorAll('.langley-cart__summary .langley-cart__summary-row');

  for (const row of rows) {
    const label = row.firstElementChild?.textContent?.trim().toLowerCase();
    if (label !== 'subtotal') continue;

    const value = row.lastElementChild?.textContent?.trim();
    if (value) return value;
  }

  return fallback;
}

function formatRemainingAmount(progress) {
  const threshold = Number(progress.dataset.deliveryThresholdMinor || 0);
  const subtotal = Number(progress.dataset.deliverySubtotalMinor || 0);
  const remaining = Math.max(0, threshold - subtotal);
  const currency = window.Shopify?.currency?.active;

  if (currency && typeof Intl !== 'undefined') {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency,
      }).format(remaining / 100);
    } catch (_error) {
      // Fall through to the rendered message when Intl/currency formatting is unavailable.
    }
  }

  return remainingLabelFromMessage(progress);
}

function ensureDelivery(drawer, _meta) {
  if (!drawer) return;
  // The free-delivery message belongs in the cart reassurance footer.
  // Remove legacy progress bars even after a cart section is replaced.
  drawer.querySelectorAll(
    '.langley-delivery-progress, [data-langley-parity-delivery-info]'
  ).forEach((element) => element.remove());
}

function ensureReservationNote(drawer, meta) {
  if (!drawer || meta.cartEmpty) {
    drawer?.querySelector('[data-langley-reservation-note]')?.remove();
    return;
  }

  const scroll = drawer.querySelector('.langley-cart__scroll');
  if (!scroll || drawer.querySelector('[data-langley-reservation-note]')) return;

  const note = document.createElement('div');
  note.className = 'langley-reservation-note';
  note.dataset.langleyReservationNote = '';
  note.innerHTML = `${infoIcon}<span><strong>Items in your bag are not reserved.</strong> Availability can change until checkout is complete.</span>`;
  scroll.before(note);
}

function enhanceCartItems(drawer, meta) {
  if (!drawer || !Array.isArray(meta.items)) return;

  const byKey = new Map(meta.items.map((item) => [String(item.key), item]));
  drawer.querySelectorAll('.cart-items__table-row[data-key]').forEach((row) => {
    const item = byKey.get(String(row.dataset.key || ''));
    if (!item) return;

    const details = row.querySelector('.cart-items__details');
    if (!details) return;

    let badge = details.querySelector('.langley-cart__new-badge');
    if (item.isNew) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'langley-cart__new-badge';
        badge.textContent = 'New';
        details.prepend(badge);
      }
    } else if (badge) {
      badge.remove();
    }

    let stock = details.querySelector('.langley-cart__stock-note');
    const lowStock = Number(item.lowStock || 0);
    if (lowStock > 0) {
      const text = lowStock === 1 ? 'Only 1 left' : `Only ${lowStock} left`;
      if (!stock) {
        stock = document.createElement('p');
        stock.className = 'langley-cart__stock-note';
        const variants = details.querySelector('.cart-items__variants');
        if (variants) variants.after(stock);
        else details.append(stock);
      }
      if (stock.textContent !== text) stock.textContent = text;
    } else if (stock) {
      stock.remove();
    }
  });
}

function openDiscount(discount) {
  if (!discount) return;

  discount.hidden = false;

  discount.querySelectorAll('details').forEach((details) => {
    details.open = true;
    details.setAttribute('open', '');
  });

  discount.querySelectorAll('.details-content, .disclosure-content').forEach((content) => {
    content.removeAttribute('inert');
    content.style.gridTemplateRows = '1fr';
    content.style.blockSize = 'auto';
    content.style.height = 'auto';
    content.style.opacity = '1';
    content.style.overflow = 'visible';
    content.style.visibility = 'visible';
  });

  const disclosureTrigger = discount.querySelector('.disclosure-trigger');
  if (disclosureTrigger) {
    disclosureTrigger.hidden = true;
    disclosureTrigger.setAttribute('aria-expanded', 'true');
  }

  const component = discount.querySelector('cart-discount-component');
  if (component) {
    component.hidden = false;
    component.removeAttribute('inert');
  }
}

function ensureDiscount(section, drawer, meta) {
  if (!drawer || meta.cartEmpty) return;

  const scroll = drawer.querySelector('.langley-cart__scroll');
  if (!scroll) return;

  let wrapper = drawer.querySelector('[data-langley-parity-discount-source]');

  if (!wrapper) {
    wrapper = section.querySelector('[data-langley-parity-discount-source]');
  }

  if (!wrapper) {
    const nativeDiscount = drawer.querySelector('.langley-cart__actions cart-discount-component');
    if (nativeDiscount) {
      wrapper = document.createElement('div');
      wrapper.className = 'langley-parity-discount';
      wrapper.dataset.langleyParityDiscountSource = '';

      const heading = document.createElement('h3');
      heading.className = 'langley-parity-discount__heading';
      heading.textContent = 'Discount code';

      const helper = document.createElement('p');
      helper.className = 'langley-parity-discount__helper';
      helper.textContent = 'Enter a valid code and we’ll update your total.';

      wrapper.append(heading, nativeDiscount, helper);
    }
  }

  if (!wrapper) return;

  if (!drawer.contains(wrapper) || wrapper.parentElement !== scroll) {
    scroll.append(wrapper);
  }

  openDiscount(wrapper);
}

function ensureOrderSummary(drawer, meta) {
  if (!drawer || meta.cartEmpty) return;

  const summary = drawer.querySelector('.langley-cart__summary');
  const totals = summary?.querySelector('.langley-cart__totals');
  if (!summary || !totals) return;

  if (!summary.querySelector('.langley-cart__summary-heading')) {
    const heading = document.createElement('h3');
    heading.className = 'langley-cart__summary-heading';
    heading.textContent = 'Order Summary';
    totals.before(heading);
  }

  const checkout = summary.querySelector('.langley-cart__checkout');
  if (checkout && !checkout.querySelector('[data-langley-secure-lock]')) {
    checkout.insertAdjacentHTML('afterbegin', lockIcon);
  }
}

function enhance(script) {
  const meta = parseMeta(script);
  if (!meta) return;

  const { section, drawer } = getContext(script);
  if (!drawer) return;

  ensureDelivery(drawer, meta);
  ensureReservationNote(drawer, meta);
  enhanceCartItems(drawer, meta);
  ensureDiscount(section, drawer, meta);
  ensureOrderSummary(drawer, meta);
}

function enhanceAll() {
  document.querySelectorAll(META_SELECTOR).forEach(enhance);
}

let queued = false;
function queueEnhance() {
  if (queued) return;
  queued = true;
  requestAnimationFrame(() => {
    queued = false;
    enhanceAll();
  });
}

if (!window.__langleyCartParityObserver) {
  window.__langleyCartParityObserver = true;
  const observer = new MutationObserver(queueEnhance);
  observer.observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('DOMContentLoaded', queueEnhance, { once: true });
  queueEnhance();
} else {
  queueEnhance();
}
